/**
 * Drawer — 오른쪽 슬라이드 패널 컴포넌트
 *
 * - createPortal로 document.body에 렌더링
 * - 백드롭 클릭, ESC 키, X 버튼으로 닫힘 (각각 옵션 제어 가능)
 * - 열림/닫힘 시 슬라이드 애니메이션 적용
 */

import { CSSProperties, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../../lib/cn.js'
import styles from './Drawer.module.css'

interface DrawerProps {
  /** 드로어 열림 여부 */
  open: boolean
  /** 닫기 요청 콜백 */
  onClose: () => void
  /** 헤더 타이틀 텍스트 */
  title?: string
  /** 드로어 너비 변형. 기본값 md (sm=360 md=480 lg=600) */
  size?: 'sm' | 'md' | 'lg'
  /** 커스텀 너비. 숫자(px) 또는 문자열(%, rem 등). size보다 우선 */
  width?: number | string
  /** 슬라이드 애니메이션 지속시간(ms). 기본값 300 */
  duration?: number
  /** 백드롭 클릭 시 닫기. 기본값 true */
  closeOnBackdrop?: boolean
  /** ESC 키 닫기. 기본값 true */
  closeOnEscape?: boolean
  /** X 닫기 버튼 표시. 기본값 true */
  showCloseButton?: boolean
  className?: string
  children: ReactNode
}

export function Drawer({
  open,
  onClose,
  title,
  size = 'md',
  width,
  duration,
  closeOnBackdrop = true,
  closeOnEscape = true,
  showCloseButton = true,
  className,
  children,
}: DrawerProps) {
  const [mounted, setMounted] = useState(false)
  const [exiting, setExiting] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)

  const panelStyle = useMemo<CSSProperties>(() => {
    const vars: CSSProperties = {}
    if (width != null) {
      vars.maxWidth = typeof width === 'number' ? `${width}px` : width
    }
    if (duration != null) {
      (vars as Record<string, string>)['--drawer-duration'] = `${duration}ms`
    }
    return vars
  }, [width, duration])

  // open 변경 시 mount/exit 제어
  useEffect(() => {
    if (open) {
      setMounted(true)
      setExiting(false)
    } else if (mounted) {
      setExiting(true)
    }
  }, [open, mounted])

  // exit 애니메이션 종료 후 언마운트
  const handleAnimationEnd = useCallback(() => {
    if (exiting) {
      setMounted(false)
      setExiting(false)
    }
  }, [exiting])

  // ESC 키 이벤트 등록
  useEffect(() => {
    if (!mounted || exiting || !closeOnEscape) return

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [mounted, exiting, closeOnEscape, onClose])

  // 스크롤 잠금
  useEffect(() => {
    if (mounted) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [mounted])

  if (!mounted) return null

  return createPortal(
    <div
      className={cn(styles.backdrop, exiting && styles.backdropExit)}
      onClick={closeOnBackdrop ? onClose : undefined}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        ref={panelRef}
        className={cn(styles.panel, !width && styles[size], exiting && styles.panelExit, className)}
        style={panelStyle}
        onClick={(e) => e.stopPropagation()}
        onAnimationEnd={handleAnimationEnd}
      >
        <div className={styles.header}>
          {title ? <h2 className={styles.title}>{title}</h2> : <div />}
          {showCloseButton && (
            <button className={styles.closeBtn} onClick={onClose} aria-label="닫기">
              ✕
            </button>
          )}
        </div>
        <div className={styles.body}>{children}</div>
      </div>
    </div>,
    document.body,
  )
}
